<template>
    <span>
        <a
            v-if="canExport"
            class="qr-trigger icon-export"
            href="#"
            title="Export Encryption Key"
            :data-test-id="`encryption-key-${encryptionKeyId}-action-qr`"
            @click.prevent="openModal"
        />

        <prime-dialog
            :visible="modalVisible"
            :header="dialogHeader"
            class="encryption-key-qr-dialog"
            data-test-id="encryption-key-qr-dialog"
            :draggable="false"
            :dismissableMask="false"
            :style="{ width: '480px', maxWidth: '92vw' }"
            modal
            @update:visible="handleVisibleChange"
        >
            <div
                class="qr-body"
                tabindex="-1"
                autofocus
            >
                <div
                    v-if="loading"
                    class="qr-loading"
                    data-test-id="qr-modal-loading"
                >
                    <progress-spinner />
                </div>

                <template v-else-if="qrData">
                    <!-- Step 1/2: the encryption key QR -->
                    <div
                        v-if="step === 1"
                        class="qr-step"
                        data-test-id="qr-modal-step1"
                    >
                        <p class="qr-help">
                            Scan this QR to import the Encryption Key. Select Next to show the validation QR.
                        </p>

                        <hr class="qr-divider" />

                        <div class="qr-image-wrap">
                            <prime-image
                                ref="keyImageRef"
                                :src="qrData.key_qr"
                                alt="Encryption key QR code"
                                image-class="qr-image"
                                preview
                                data-test-id="qr-key-image"
                            >
                                <template #original="slotProps">
                                    <img
                                        :src="qrData.key_qr"
                                        :class="slotProps.class"
                                        :style="slotProps.style"
                                        alt="Encryption key QR code"
                                        title="Click to close"
                                    />
                                </template>
                            </prime-image>
                        </div>

                        <p
                            class="qr-zoom-hint"
                            data-test-id="qr-key-zoom-hint"
                        >
                            Click the QR code to enlarge it.
                        </p>

                        <p
                            v-if="qrData.name_truncated"
                            class="qr-warning"
                            data-test-id="qr-name-truncated-warning"
                        >
                            The key name was shortened to &ldquo;{{ qrData.name }}&rdquo; to fit the device's
                            10-character limit.
                        </p>

                        <div class="qr-footer">
                            <button
                                class="btn btn-primary"
                                data-test-id="qr-modal-next"
                                @click="step = 2"
                            >
                                Next
                            </button>
                        </div>
                    </div>

                    <!-- Step 2/2: the validation passphrase -->
                    <div
                        v-else
                        class="qr-step"
                        data-test-id="qr-modal-step2"
                    >
                        <select-button
                            v-model="passphraseView"
                            class="qr-passphrase-toggle"
                            :allowEmpty="false"
                            :options="passphraseViewOptions"
                            optionValue="value"
                            optionLabel="label"
                            data-test-id="qr-passphrase-toggle"
                        >
                            <template #option="slotProps">
                                <span :data-test-id="`qr-passphrase-toggle-${slotProps.option.value}`">
                                    {{ slotProps.option.label }}
                                </span>
                            </template>
                        </select-button>

                        <p
                            class="qr-help"
                            data-test-id="qr-passphrase-help"
                        >
                            {{ passphraseHelp }} If you close this screen before validating the key, you will need to
                            generate a new QR code.
                        </p>

                        <hr class="qr-divider" />

                        <div
                            v-if="passphraseView === 'qr'"
                            class="qr-image-wrap"
                            data-test-id="qr-passphrase-qr"
                        >
                            <prime-image
                                ref="passphraseImageRef"
                                :src="qrData.passphrase_qr"
                                alt="Passphrase QR code"
                                image-class="qr-image"
                                preview
                            >
                                <template #original="slotProps">
                                    <img
                                        :src="qrData.passphrase_qr"
                                        :class="slotProps.class"
                                        :style="slotProps.style"
                                        alt="Passphrase QR code"
                                        title="Click to close"
                                    />
                                </template>
                            </prime-image>
                        </div>

                        <p
                            v-if="passphraseView === 'qr'"
                            class="qr-zoom-hint"
                        >
                            Click the QR code to enlarge it.
                        </p>

                        <div
                            v-else
                            data-test-id="qr-passphrase-text"
                        >
                            <ol class="qr-word-grid">
                                <li
                                    v-for="(word, index) in words"
                                    :key="index"
                                >
                                    <span class="qr-word-num">
                                        {{ index + 1 }}
                                    </span>
                                    <span
                                        class="qr-word"
                                        :data-test-id="`qr-passphrase-word-${index + 1}`"
                                    >
                                        {{ word }}
                                    </span>
                                </li>
                            </ol>
                        </div>

                        <div class="qr-footer qr-footer--split">
                            <button
                                class="btn"
                                data-test-id="qr-modal-back"
                                @click="step = 1"
                            >
                                Back
                            </button>
                            <button
                                class="btn btn-primary"
                                data-test-id="qr-modal-done"
                                @click="closeModal"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </template>
            </div>
        </prime-dialog>
    </span>
</template>

<script>
import Dialog from 'primevue/dialog'
import Image from 'primevue/image'
import SelectButton from 'primevue/selectbutton'
import ProgressSpinner from 'primevue/progressspinner'
import canConfirm from '@/mixins/canConfirm'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import canToast from '@/mixins/canToast'
import featureFlags from '@/constants/feature-flags'

export default {
    name: 'EncryptionKeyQrModal',

    components: {
        PrimeDialog: Dialog,
        PrimeImage: Image,
        SelectButton,
        ProgressSpinner,
    },

    mixins: [canConfirm, needsOrganizationSlug, canToast],

    props: {
        encryptionKeyId: {
            type: Number,
            required: true,
        },
    },

    data() {
        return {
            modalVisible: false,
            loading: false,
            step: 1,
            passphraseView: 'qr',
            qrData: null,
            passphraseViewOptions: [
                { label: 'QR Code', value: 'qr' },
                { label: 'Passphrase', value: 'text' },
            ],
        }
    },

    computed: {
        canExport() {
            const { hasPermission, hasFeatureFlag } = this.$store.getters

            return hasPermission('export encryption keys') && hasFeatureFlag(featureFlags.ENCRYPTION_KEY_EXPORT_ENABLED)
        },

        dialogHeader() {
            return `Export Encryption Key: ${this.step}/2`
        },

        words() {
            return this.qrData ? this.qrData.passphrase.split(' ') : []
        },

        // Step 2 can show either the validation QR or the passphrase words, so
        // the help text has to describe whichever one is on screen.
        passphraseHelp() {
            return this.passphraseView === 'qr'
                ? 'Scan this QR to validate the Encryption Key.'
                : 'Type these words, in this order, to validate the Encryption Key.'
        },
    },

    beforeUnmount() {
        document.removeEventListener('keydown', this.onEscapeCapture, true)
    },

    methods: {
        openModal() {
            this.step = 1
            this.passphraseView = 'qr'
            this.qrData = null
            this.modalVisible = true
            // Capture Escape before the PrimeVue Dialog's document listener so
            // that, while a QR preview is open, Escape closes the preview only.
            document.addEventListener('keydown', this.onEscapeCapture, true)
            this.fetchQr()
        },

        // While a QR preview is open, handle Escape ourselves (close the preview)
        // and stop it reaching the Dialog. When no preview is open, let it through
        // so the Dialog's Escape handling shows the close confirmation.
        onEscapeCapture(event) {
            if (event.key !== 'Escape') {
                return
            }

            const imagePreview = this.visibleImagePreview()
            if (!imagePreview) {
                return
            }

            event.preventDefault()
            event.stopImmediatePropagation()
            imagePreview.hidePreview()
        },

        visibleImagePreview() {
            const images = [this.$refs.keyImageRef, this.$refs.passphraseImageRef]

            return images.find((image) => image && (image.maskVisible || image.previewVisible)) || null
        },

        fetchQr() {
            this.loading = true

            this.$http
                .post(`${this.organizationSlug}/encryption-keys/${this.encryptionKeyId}/qr`)
                .then(({ data }) => {
                    this.qrData = data
                    this.loading = false
                })
                .catch(() => {
                    this.loading = false
                    this.toast('Could not generate QR code', 'error')
                    this.closeModal()
                })
        },

        // The dialog close button and escape key both ask to hide the modal;
        // warn before discarding a generated export, since closing forces a new
        // passphrase for anyone who has not yet validated the key.
        handleVisibleChange(visible) {
            if (visible) {
                this.modalVisible = true
                return
            }

            this.requestClose()
        },

        async requestClose() {
            if (!this.qrData) {
                this.closeModal()
                return
            }

            const confirmed = await this.confirm({
                header: 'Close QR export?',
                message:
                    'If you close this you will have to generate a new QR ' +
                    'code. Anyone who has not yet validated this key will ' +
                    'need it re-sent with a new passphrase.',
                acceptLabel: 'Close',
                danger: true,
            })

            if (confirmed) {
                this.closeModal()
            }
        },

        closeModal() {
            document.removeEventListener('keydown', this.onEscapeCapture, true)
            this.modalVisible = false
            this.qrData = null
            this.step = 1
            this.passphraseView = 'qr'
        },
    },
}
</script>

<style lang="scss">
.encryption-key-qr-dialog.p-dialog {
    background-color: #434a4d;
    color: #ccc;
    border: 1px solid #43474a;

    .p-dialog-title {
        color: #fff;
    }

    // Keep the close button's circular border visible on every step, not just
    // while it happens to be focused, and match it to the X icon's color.
    .p-dialog-close-button {
        border: 1px solid currentColor;
        border-radius: 50%;
    }
}
</style>

<style lang="scss" scoped>
// Circular icon button, matching the other action icons in the app.
.qr-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border: 1px solid #888;
    border-radius: 50%;
    font-size: 1rem;
    color: #ddd;
    text-decoration: none;
    vertical-align: middle;
    cursor: pointer;

    &:hover {
        background-color: #888;
        color: #fff;
    }
}

// Hold a fixed height so the modal never resizes when moving between steps
// or between the passphrase QR and word list; overflow scrolls internally.
.qr-body {
    height: 580px;
    display: flex;
    flex-direction: column;

    // Receives the dialog's initial focus (instead of the close button) so the
    // close button does not show a focus ring on top of its border.
    &:focus,
    &:focus-visible {
        outline: none;
    }
}

.qr-step {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
}

.qr-loading {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    padding: 40px 0;
}

// Make the active toggle option clearly stand out from the inactive one.
.qr-passphrase-toggle {
    :deep(.p-togglebutton) {
        border-color: #888;
    }

    :deep(.p-togglebutton),
    :deep(.p-togglebutton .p-togglebutton-label) {
        background: transparent;
        color: #bbb;
        font-weight: 500;
    }

    :deep(.p-togglebutton.p-togglebutton-checked),
    :deep(.p-togglebutton.p-togglebutton-checked .p-togglebutton-content),
    :deep(.p-togglebutton.p-togglebutton-checked .p-togglebutton-label) {
        background: #212628;
        color: #fff;
        font-weight: 700;
    }

    :deep(.p-togglebutton.p-togglebutton-checked .p-togglebutton-content) {
        box-shadow: none;
    }
}

.qr-help {
    margin-bottom: 16px;
    font-size: 0.9rem;
    line-height: 1.4;
}

.qr-divider {
    border: none;
    border-top: 1px solid rgba(255, 255, 255, 0.15);
    margin: 0 0 16px;
}

.qr-zoom-hint {
    margin: 8px 0 0;
    text-align: center;
    font-size: 0.8rem;
    opacity: 0.65;
}

.qr-image-wrap {
    display: flex;
    justify-content: center;
    margin-bottom: 16px;

    :deep(.qr-image) {
        width: 320px;
        max-width: 100%;
        height: auto;
        background: #fff;
        padding: 12px;
        border-radius: 6px;
    }
}

.qr-warning {
    margin-bottom: 12px;
    margin-top: 12px;
    padding: 8px 12px;
    border-radius: 4px;
    background-color: rgba(255, 193, 7, 0.15);
    color: #ffce54;
    font-size: 0.85rem;
}

.qr-passphrase-toggle {
    display: flex;
    margin-bottom: 16px;
}

.qr-word-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px 16px;
    margin: 0 0 16px;
    padding: 0;
    list-style: none;

    li {
        display: flex;
        align-items: center;
        gap: 10px;
    }
}

.qr-word-num {
    text-align: right;
    opacity: 0.6;
    font-size: 0.85rem;
}

.qr-word {
    flex: 1;
    padding: 8px 12px;
    border-radius: 4px;
    background-color: #2f3538;
    color: #fff;
    font-family: monospace;
}

.qr-footer {
    display: flex;
    justify-content: flex-end;
    margin-top: auto;
    padding-top: 16px;
}

.qr-footer--split {
    justify-content: space-between;
}
</style>
